/**
 * ExportPanel - Panel universal de exportación para todas las herramientas
 * Captura el contenido real de la ventana y permite seleccionar qué incluir
 */
import React, { useState, useEffect } from 'react';
import { X, FilePdf, Printer, Check, FileText, ChartLine, Table, ListBullets, Info, CurrencyEur, Camera } from '@phosphor-icons/react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import DOMPurify from 'dompurify';

// Función para escapar HTML y prevenir XSS
const escapeHtml = (text) => {
  if (!text) return '';
  return DOMPurify.sanitize(String(text), { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};

// Configuración de secciones por tipo de ventana
const WINDOW_SECTIONS = {
  livePortfolio: {
    title: 'Cartera en Vivo',
    sections: [
      { id: 'summary', label: 'Resumen (valor total, beneficio)', icon: CurrencyEur, default: true },
      { id: 'chart', label: 'Gráfico de evolución', icon: ChartLine, default: true },
      { id: 'table', label: 'Tabla de activos (Plano)', icon: Table, default: true }
    ]
  },
  comparator: {
    title: 'Comparador de Activos',
    sections: [
      { id: 'table', label: 'Tabla comparativa (precios, máx/mín)', icon: Table, default: true },
      { id: 'chart', label: 'Gráfico comparativo', icon: ChartLine, default: true }
    ]
  },
  portfolio: {
    title: 'Resumen de Cartera',
    sections: [
      { id: 'summary', label: 'Resumen general', icon: CurrencyEur, default: true },
      { id: 'chart', label: 'Distribución por activo', icon: ChartLine, default: true },
      { id: 'table', label: 'Lista de posiciones', icon: Table, default: true }
    ]
  },
  indicadores: {
    title: 'Análisis Indicadores Técnicos',
    sections: [
      { id: 'indicators', label: 'Todos los indicadores', icon: ChartLine, default: true },
      { id: 'summary', label: 'Resumen de señales', icon: Info, default: true }
    ]
  },
  rendimiento: {
    title: 'Análisis de Rendimiento',
    sections: [
      { id: 'metrics', label: 'Métricas principales', icon: CurrencyEur, default: true },
      { id: 'chart', label: 'Gráfico de rendimiento', icon: ChartLine, default: true }
    ]
  },
  signals: {
    title: 'Análisis Señales Trading',
    sections: [
      { id: 'signals', label: 'Señales actuales', icon: Info, default: true },
      { id: 'analysis', label: 'Análisis técnico', icon: ChartLine, default: true }
    ]
  },
  calculators: {
    title: 'Calculadoras',
    sections: [
      { id: 'calculator', label: 'Calculadora actual', icon: CurrencyEur, default: true }
    ]
  },
  indices: {
    title: 'Comparación con Índices',
    sections: [
      { id: 'comparison', label: 'Comparativa completa', icon: ChartLine, default: true }
    ]
  },
  backtesting: {
    title: 'Backtesting',
    sections: [
      { id: 'results', label: 'Resultados del backtest', icon: Table, default: true },
      { id: 'chart', label: 'Gráfico de rendimiento', icon: ChartLine, default: true }
    ]
  },
  sectors: {
    title: 'Análisis por Sectores',
    sections: [
      { id: 'sectors', label: 'Vista de sectores', icon: ChartLine, default: true }
    ]
  },
  news: {
    title: 'Noticias del Mercado',
    sections: [
      { id: 'news', label: 'Noticias', icon: FileText, default: true }
    ]
  },
  analyzer: {
    title: 'Analizador de Activos',
    sections: [
      { id: 'analysis', label: 'Análisis completo', icon: Info, default: true }
    ]
  },
  alerts: {
    title: 'Alertas',
    sections: [
      { id: 'alerts', label: 'Lista de alertas', icon: Info, default: true }
    ]
  },
  extremes: {
    title: 'Historial Máx/Mín',
    sections: [
      { id: 'extremes', label: 'Tabla de extremos', icon: Table, default: true }
    ]
  },
  heatmap: {
    title: 'Mapa de Correlaciones',
    sections: [
      { id: 'heatmap', label: 'Matriz de correlación', icon: Table, default: true }
    ]
  },
  candlestickChart: {
    title: 'Gráfico de Velas',
    sections: [
      { id: 'chart', label: 'Gráfico completo', icon: ChartLine, default: true }
    ]
  },
  indicadoresEnhanced: {
    title: 'Análisis Técnico Avanzado',
    sections: [
      { id: 'technicals', label: 'Indicadores técnicos', icon: ChartLine, default: true }
    ]
  },
  categories: {
    title: 'Categorías',
    sections: [
      { id: 'categories', label: 'Lista de categorías', icon: ListBullets, default: true }
    ]
  }
};

// Configuración por defecto
const DEFAULT_SECTIONS = {
  title: 'Exportar Contenido',
  sections: [
    { id: 'content', label: 'Contenido visible', icon: FileText, default: true }
  ]
};

const ExportPanel = ({ 
  isOpen, 
  onClose, 
  windowId, 
  windowTitle,
  windowRef, // Referencia al elemento de la ventana para capturar
  onExport
}) => {
  const config = WINDOW_SECTIONS[windowId] || DEFAULT_SECTIONS;
  const [selectedSections, setSelectedSections] = useState({});
  const [isExporting, setIsExporting] = useState(false);
  const [exportMode, setExportMode] = useState('capture'); // 'capture' = captura de pantalla

  // Inicializar secciones seleccionadas
  useEffect(() => {
    if (isOpen) {
      const defaults = {};
      config.sections.forEach(section => {
        defaults[section.id] = section.default;
      });
      setSelectedSections(defaults);
    }
  }, [isOpen, config.sections]);

  const toggleSection = (sectionId) => {
    setSelectedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const selectAll = () => {
    const all = {};
    config.sections.forEach(section => {
      all[section.id] = true;
    });
    setSelectedSections(all);
  };

  const selectNone = () => {
    const none = {};
    config.sections.forEach(section => {
      none[section.id] = false;
    });
    setSelectedSections(none);
  };

  const hasSelection = Object.values(selectedSections).some(v => v);

  // Exportar PDF capturando la ventana como imagen
  const handleExportPDF = async () => {
    if (!hasSelection) {
      toast.error('Selecciona al menos una sección');
      return;
    }

    setIsExporting(true);
    onClose(); // Cerrar el panel para capturar la ventana limpia
    
    // Esperar a que se cierre el panel
    await new Promise(r => setTimeout(r, 300));
    
    try {
      toast.info('Capturando contenido...');
      
      const html2canvas = (await import('html2canvas')).default;
      
      // Buscar la ventana padre
      const windowElement = document.querySelector(`[data-testid="floating-window-${windowId}"]`);
      
      if (!windowElement) {
        toast.error('No se pudo encontrar la ventana');
        setIsExporting(false);
        return;
      }
      
      // Capturar la ventana
      const canvas = await html2canvas(windowElement, {
        scale: 2,
        backgroundColor: '#0a0f1a',
        useCORS: true,
        logging: false,
        allowTaint: true,
        onclone: (clonedDoc, clonedElement) => {
          clonedElement.style.height = 'auto';
          clonedElement.style.maxHeight = 'none';
          clonedElement.style.overflow = 'visible';
          clonedElement.style.position = 'relative';
          const contentDiv = clonedElement.querySelector('.window-content');
          if (contentDiv) { 
            contentDiv.style.height = 'auto'; 
            contentDiv.style.maxHeight = 'none'; 
            contentDiv.style.overflow = 'visible'; 
          }
          const hiddenDivs = clonedElement.querySelectorAll('[class*="overflow-hidden"]');
          hiddenDivs.forEach(div => { 
            div.style.overflow = 'visible'; 
            div.style.height = 'auto'; 
            div.style.maxHeight = 'none'; 
          });
        }
      });
      
      // Crear PDF
      const imgData = canvas.toDataURL('image/png');
      const isLandscape = canvas.width > canvas.height * 0.85;
      const pdf = new jsPDF(isLandscape ? 'l' : 'p', 'mm', 'a4');
      
      const pageWidth = isLandscape ? 297 : 210;
      const pageHeight = isLandscape ? 210 : 297;
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);
      const contentHeight = pageHeight - (margin * 2) - 15; // Espacio para título
      
      // Título
      pdf.setFillColor(10, 15, 26);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      
      pdf.setTextColor(0, 220, 255);
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text(windowTitle || config.title, pageWidth / 2, margin + 5, { align: 'center' });
      
      pdf.setFontSize(9);
      pdf.setTextColor(150, 150, 150);
      pdf.text(`Generado: ${new Date().toLocaleString('es-ES')}`, pageWidth / 2, margin + 12, { align: 'center' });
      
      // Calcular dimensiones de la imagen
      const imgRatio = canvas.width / canvas.height;
      const availableRatio = contentWidth / contentHeight;
      
      let imgWidth, imgHeight;
      if (imgRatio > availableRatio) {
        imgWidth = contentWidth;
        imgHeight = contentWidth / imgRatio;
      } else {
        imgHeight = contentHeight;
        imgWidth = contentHeight * imgRatio;
      }
      
      const xOffset = margin + (contentWidth - imgWidth) / 2;
      const yOffset = margin + 18;
      
      pdf.addImage(imgData, 'PNG', xOffset, yOffset, imgWidth, imgHeight);
      
      // Footer
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);
      pdf.text('KAPITAL - Análisis Técnico de Activos', pageWidth / 2, pageHeight - 5, { align: 'center' });
      
      // Guardar
      const filename = `${(windowTitle || config.title).replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(filename);
      
      toast.success('PDF exportado correctamente');
      
      if (onExport) {
        onExport({ type: 'pdf', sections: Object.keys(selectedSections).filter(k => selectedSections[k]), filename });
      }
    } catch (error) {
      console.error('Error exportando PDF:', error);
      toast.error('Error al exportar PDF');
    } finally {
      setIsExporting(false);
    }
  };

  // Imprimir capturando la ventana
  const handlePrint = async () => {
    if (!hasSelection) {
      toast.error('Selecciona al menos una sección');
      return;
    }

    setIsExporting(true);
    onClose(); // Cerrar el panel para capturar limpio
    
    await new Promise(r => setTimeout(r, 300));
    
    try {
      toast.info('Preparando impresión...');
      
      const html2canvas = (await import('html2canvas')).default;
      
      const windowElement = document.querySelector(`[data-testid="floating-window-${windowId}"]`);
      
      if (!windowElement) {
        toast.error('No se pudo encontrar la ventana');
        setIsExporting(false);
        return;
      }
      
      const canvas = await html2canvas(windowElement, {
        scale: 2,
        backgroundColor: '#0a0f1a',
        useCORS: true,
        logging: false,
        allowTaint: true,
        onclone: (clonedDoc, clonedElement) => {
          clonedElement.style.height = 'auto';
          clonedElement.style.maxHeight = 'none';
          clonedElement.style.overflow = 'visible';
          clonedElement.style.position = 'relative';
          const contentDiv = clonedElement.querySelector('.window-content');
          if (contentDiv) { 
            contentDiv.style.height = 'auto'; 
            contentDiv.style.maxHeight = 'none'; 
            contentDiv.style.overflow = 'visible'; 
          }
          const hiddenDivs = clonedElement.querySelectorAll('[class*="overflow-hidden"]');
          hiddenDivs.forEach(div => { 
            div.style.overflow = 'visible'; 
            div.style.height = 'auto'; 
            div.style.maxHeight = 'none'; 
          });
        }
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Sanitizar título para prevenir XSS
      const safeTitle = escapeHtml(windowTitle || config.title);
      const safeDate = escapeHtml(new Date().toLocaleString('es-ES'));
      
      // Abrir ventana de impresión con contenido seguro
      const printWindow = window.open('', '_blank', 'width=1000,height=800');
      if (printWindow) {
        const doc = printWindow.document;
        doc.open();
        
        // Crear estructura del documento de forma segura
        const html = doc.createElement('html');
        const head = doc.createElement('head');
        const title = doc.createElement('title');
        title.textContent = safeTitle;
        
        const style = doc.createElement('style');
        style.textContent = `
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { 
            background: white; 
            padding: 20px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          }
          h1 { 
            color: #0891b2; 
            font-size: 18px; 
            margin-bottom: 5px;
            text-align: center;
          }
          .meta {
            color: #666;
            font-size: 11px;
            text-align: center;
            margin-bottom: 15px;
          }
          img { 
            max-width: 100%; 
            height: auto; 
            display: block;
            margin: 0 auto;
            border: 1px solid #ddd;
            border-radius: 8px;
          }
          @media print { 
            body { padding: 10px; } 
            img { border: none; max-width: 100%; }
          }
        `;
        
        head.appendChild(title);
        head.appendChild(style);
        
        const body = doc.createElement('body');
        const h1 = doc.createElement('h1');
        h1.textContent = safeTitle;
        
        const meta = doc.createElement('p');
        meta.className = 'meta';
        meta.textContent = `Generado: ${safeDate}`;
        
        const img = doc.createElement('img');
        img.src = imgData;
        img.alt = safeTitle;
        
        body.appendChild(h1);
        body.appendChild(meta);
        body.appendChild(img);
        
        html.appendChild(head);
        html.appendChild(body);
        
        doc.appendChild(doc.implementation.createDocumentType('html', '', ''));
        doc.appendChild(html);
        doc.close();
        
        // Imprimir después de cargar
        printWindow.onload = () => {
          setTimeout(() => printWindow.print(), 500);
        };
      }
      
      toast.success('Ventana de impresión abierta');
      
      if (onExport) {
        onExport({ type: 'print', sections: Object.keys(selectedSections).filter(k => selectedSections[k]) });
      }
    } catch (error) {
      console.error('Error preparando impresión:', error);
      toast.error('Error al preparar la impresión');
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className="relative w-full max-w-md mx-4 rounded-xl overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, rgba(15, 25, 45, 0.98) 0%, rgba(10, 18, 35, 0.99) 100%)',
          border: '1px solid rgba(0, 220, 255, 0.3)',
          boxShadow: '0 0 40px rgba(0, 220, 255, 0.15), 0 20px 60px rgba(0, 0, 0, 0.5)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gold-500/20">
          <div className="flex items-center gap-3">
            <Camera size={24} className="text-gold-400" />
            <div>
              <h3 className="text-white font-semibold">Exportar / Imprimir</h3>
              <p className="text-xs text-gray-400">{windowTitle || config.title}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenido */}
        <div className="p-5">
          {/* Info */}
          <div className="mb-4 p-3 rounded-lg bg-gold-500/10 border border-gold-500/20">
            <p className="text-xs text-gold-300">
              Se capturará la ventana tal como se ve actualmente. Asegúrate de que el contenido que quieres exportar sea visible.
            </p>
          </div>

          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-300">Confirma qué incluir:</p>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="text-xs text-gold-400 hover:text-gold-300 transition-colors"
              >
                Todos
              </button>
              <span className="text-gray-600">|</span>
              <button
                onClick={selectNone}
                className="text-xs text-gray-400 hover:text-gray-300 transition-colors"
              >
                Ninguno
              </button>
            </div>
          </div>

          {/* Lista de secciones */}
          <div className="space-y-2 max-h-[250px] overflow-y-auto pr-2">
            {config.sections.map((section) => {
              const Icon = section.icon;
              const isSelected = selectedSections[section.id];
              
              return (
                <button
                  key={section.id}
                  onClick={() => toggleSection(section.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                    isSelected 
                      ? 'bg-gold-500/20 border border-gold-500/50' 
                      : 'bg-white/5 border border-transparent hover:bg-white/10'
                  }`}
                >
                  <div className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${
                    isSelected ? 'bg-gold-500 text-white' : 'bg-gray-700 text-gray-500'
                  }`}>
                    {isSelected && <Check size={14} weight="bold" />}
                  </div>
                  <Icon size={18} className={isSelected ? 'text-gold-400' : 'text-gray-500'} />
                  <span className={`text-sm ${isSelected ? 'text-white' : 'text-gray-400'}`}>
                    {section.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer con botones */}
        <div className="flex gap-3 p-5 pt-0">
          <button
            onClick={onClose}
            className="flex-1 py-3 px-4 rounded-lg text-sm font-medium text-gray-400 bg-white/5 hover:bg-white/10 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handlePrint}
            disabled={!hasSelection || isExporting}
            className="flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-medium text-zinc-400 bg-zinc-500/20 hover:bg-zinc-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Printer size={18} />
            Imprimir
          </button>
          <button
            onClick={handleExportPDF}
            disabled={!hasSelection || isExporting}
            className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-gold-600 to-gold-500 hover:from-gold-500 hover:to-gold-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Exportando...
              </>
            ) : (
              <>
                <FilePdf size={18} />
                Exportar PDF
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportPanel;
